import { senderKeys, useSenders } from '@/features/senders/hooks/useSenders';
import { sendersService } from '@/features/senders/services/senders.service';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Loader2, Mail, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BilanAttribution,
  DossierVise,
  attribuerParLots,
  nommerEchecs,
} from '../utils/attribuer-expediteur';

interface BulkSenderDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Dossiers cochés dans la liste (le nom sert à désigner un échec). */
  selectedFolders: DossierVise[];
  /** Nombre de dossiers de la liste avec les filtres en cours, toutes pages. */
  filterTotal: number;
  /** Lit tous les dossiers des filtres en cours, toutes pages confondues. */
  loadAllFilteredFolders: () => Promise<DossierVise[]>;
}

/** Au-delà, la liste des échecs se replie sur « et N autres ». */
const ECHECS_AFFICHES = 8;

type Portee = 'selection' | 'liste';

/**
 * « Choisir l'expéditeur » pour plusieurs dossiers à la fois, depuis la liste
 * des dossiers. Même effet que « Configurer » dans la fiche d'un dossier,
 * répété sur chaque dossier visé.
 */
export function BulkSenderDrawer({
  isOpen,
  onClose,
  selectedFolders,
  filterTotal,
  loadAllFilteredFolders,
}: BulkSenderDrawerProps) {
  const queryClient = useQueryClient();
  const { data: senders = [], isLoading: isLoadingSenders } = useSenders();

  const [senderUuid, setSenderUuid] = useState('');
  const [portee, setPortee] = useState<Portee>('selection');
  const [total, setTotal] = useState(0);
  const [faits, setFaits] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [bilan, setBilan] = useState<BilanAttribution | null>(null);
  const [echecsNommes, setEchecsNommes] = useState<string[]>([]);
  const [erreur, setErreur] = useState('');

  const nombreSelection = selectedFolders.length;
  // La question de la portée ne se pose que si la liste va au-delà de ce qui
  // est coché (autres pages, ou dossiers non cochés).
  const proposerListe = filterTotal > nombreSelection;
  const nombreVise = portee === 'liste' ? filterTotal : nombreSelection;
  const activeSenders = senders.filter(sender => sender.isActive);

  const handleClose = () => {
    if (isRunning) return;
    setSenderUuid('');
    setPortee('selection');
    setTotal(0);
    setFaits(0);
    setBilan(null);
    setEchecsNommes([]);
    setErreur('');
    onClose();
  };

  const handleApply = async () => {
    if (!senderUuid || isRunning) return;
    setIsRunning(true);
    setErreur('');
    setFaits(0);

    try {
      const dossiers =
        portee === 'liste' ? await loadAllFilteredFolders() : selectedFolders;
      setTotal(dossiers.length);

      const resultat = await attribuerParLots(
        dossiers.map(d => d.uuid),
        folderUuid =>
          sendersService.createConfiguration({
            senderUuid,
            folderUuid,
            priority: 2,
          }),
        setFaits
      );

      setBilan(resultat);
      setEchecsNommes(
        nommerEchecs(resultat.echecs, dossiers, resultat.raisons)
      );
      queryClient.invalidateQueries({ queryKey: ['domains'] });
      queryClient.invalidateQueries({ queryKey: ['folder'] });
      // La liste des affectations sert à avertir avant de supprimer un
      // expéditeur : elle doit refléter ce qui vient d'être appliqué.
      queryClient.invalidateQueries({ queryKey: senderKeys.configurations });
    } catch {
      setErreur(
        'La liste des dossiers n’a pas pu être lue. Réessayez dans un instant.'
      );
    } finally {
      setIsRunning(false);
    }
  };

  if (!isOpen) return null;

  const pourcentage = total > 0 ? Math.round((faits / total) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div
        className="flex-1 bg-neutral-900/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div className="flex h-full w-full max-w-xl flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-neutral-200 p-6">
          <div className="flex items-center gap-3">
            <div className="bg-primary-100 flex h-10 w-10 items-center justify-center rounded-xl">
              <Mail className="text-primary-600 h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-neutral-900">
                Choisir l’expéditeur
              </h2>
              <p className="text-sm text-neutral-500">
                {nombreVise} dossier{nombreVise > 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isRunning}
            aria-label="Fermer"
            className="rounded-lg p-2 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto p-6">
          {bilan ? (
            <div
              className={`rounded-lg border p-4 ${
                bilan.echecs.length === 0
                  ? 'border-green-200 bg-green-50'
                  : 'border-amber-200 bg-amber-50'
              }`}
            >
              <div className="flex items-center gap-2">
                {bilan.echecs.length === 0 ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                )}
                <p className="text-sm font-semibold text-neutral-900">
                  {bilan.reussis === 0
                    ? 'Aucun dossier modifié'
                    : `Expéditeur appliqué à ${bilan.reussis} dossier${bilan.reussis > 1 ? 's' : ''}`}
                </p>
              </div>
              {bilan.echecs.length > 0 && (
                <div className="mt-2 text-sm text-amber-800">
                  <p>
                    {bilan.echecs.length} dossier
                    {bilan.echecs.length > 1 ? 's n’ont' : ' n’a'} pas pu être
                    modifié{bilan.echecs.length > 1 ? 's' : ''} :
                  </p>
                  <ul className="mt-1 list-inside list-disc">
                    {echecsNommes.slice(0, ECHECS_AFFICHES).map((nom, i) => (
                      <li key={`${nom}-${i}`} className="truncate">
                        {nom}
                      </li>
                    ))}
                    {echecsNommes.length > ECHECS_AFFICHES && (
                      <li>
                        et {echecsNommes.length - ECHECS_AFFICHES} autre
                        {echecsNommes.length - ECHECS_AFFICHES > 1 ? 's' : ''}
                      </li>
                    )}
                  </ul>
                  {Object.keys(bilan.raisons).length < bilan.echecs.length && (
                    <p className="mt-1">
                      Relancez la même action pour réessayer.
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : isRunning ? (
            <div>
              <div className="mb-2 flex items-center justify-between text-sm">
                <span className="font-medium text-neutral-700">En cours…</span>
                <span className="text-neutral-500">
                  {faits}/{total || nombreVise}
                </span>
              </div>
              <div className="h-3 overflow-hidden rounded-full bg-neutral-200">
                <div
                  className="bg-primary-500 h-full rounded-full transition-all duration-300"
                  style={{ width: `${pourcentage}%` }}
                />
              </div>
            </div>
          ) : (
            <>
              {proposerListe && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-neutral-700">
                    Dossiers concernés
                  </h3>
                  {(
                    [
                      ['selection', nombreSelection, 'sélectionné'],
                      ['liste', filterTotal, 'de la liste (toutes pages)'],
                    ] as const
                  ).map(([valeur, nombre, libelle]) => (
                    <label
                      key={valeur}
                      className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 text-sm transition-colors ${
                        portee === valeur
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-neutral-200 hover:bg-neutral-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="portee-expediteur"
                        checked={portee === valeur}
                        onChange={() => setPortee(valeur)}
                      />
                      <span className="text-neutral-900">
                        {nombre} dossier{nombre > 1 ? 's' : ''}{' '}
                        {valeur === 'selection'
                          ? `${libelle}${nombre > 1 ? 's' : ''}`
                          : libelle}
                      </span>
                    </label>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-neutral-700">
                  Expéditeur
                </h3>
                {isLoadingSenders ? (
                  <div className="h-14 animate-pulse rounded-lg bg-neutral-200" />
                ) : activeSenders.length === 0 ? (
                  <p className="rounded-lg border border-dashed border-neutral-300 p-4 text-sm text-neutral-600">
                    Aucun expéditeur pour l’instant.{' '}
                    <Link
                      to="/senders"
                      className="text-primary-600 font-medium hover:underline"
                    >
                      Créer un expéditeur
                    </Link>
                  </p>
                ) : (
                  <div className="space-y-2">
                    {activeSenders.map(sender => (
                      <button
                        key={sender.uuid}
                        type="button"
                        onClick={() => setSenderUuid(sender.uuid)}
                        className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                          senderUuid === sender.uuid
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
                        }`}
                      >
                        <div className="bg-primary-50 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg">
                          <Mail className="text-primary-600 h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate font-medium text-neutral-900">
                            {sender.name}
                          </p>
                          <p className="truncate text-sm text-neutral-600">
                            {sender.email}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="border-t border-neutral-200 p-6">
          {erreur && (
            <p className="mb-3 text-center text-sm text-red-600">{erreur}</p>
          )}
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              disabled={isRunning}
              className="flex-1 rounded-lg border border-neutral-300 px-4 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:opacity-50"
            >
              {bilan ? 'Fermer' : 'Annuler'}
            </button>
            {!bilan && (
              <button
                onClick={handleApply}
                disabled={!senderUuid || isRunning || nombreVise === 0}
                className="bg-primary-600 hover:bg-primary-700 flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isRunning && <Loader2 className="h-4 w-4 animate-spin" />}
                Appliquer à {nombreVise} dossier{nombreVise > 1 ? 's' : ''}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
